// Permission definitions based on role
export const PERMISSIONS = {
  ADD_ASSETS: 'add_assets',
  EDIT_ASSETS: 'edit_assets',
  CHECKOUT: 'checkout',
  CHECKIN: 'checkin',
  ADD_EMPLOYEE: 'add_employee',
  EDIT_EMPLOYEE: 'edit_employee',
  DELETE_EMPLOYEE: 'delete_employee',
  MANAGE_EMPLOYEE_PERMISSIONS: 'manage_employee_permissions',
  VIEW_ALL_ASSETS: 'view_all_assets',
  VIEW_ASSIGNED_ASSETS: 'view_assigned_assets',
  /** Acknowledge / view pending handovers (Settings → Asset Handover module). */
  VIEW_MY_ASSET_HANDOVER: 'view_my_asset_handover',
  DELETE_ASSETS: 'delete_assets',
  /** Site/category/history asset reports (Reports hub + asset list export). */
  VIEW_ASSET_REPORTS: 'view_asset_reports',
  /** Feedback & performance report pages. */
  VIEW_PERFORMANCE_REPORTS: 'view_performance_reports',

  ADD_TEAM: 'add_team',
  EDIT_TEAM: 'edit_team',
  DELETE_TEAM: 'delete_team',
  VIEW_TEAM_EVENT_FEEDBACK: 'view_team_feedback',
  VIEW_TEAM_FEEDBACK: 'view_team_feedback',
  SHOW_ALL_TEAMS: 'show_all_teams',

  ADD_MATCH: 'add_match',
  EDIT_MATCH: 'edit_match',

  GIVE_MATCH_TL_FEEDBACK: 'give_match_tl_feedback',
  VIEW_MATCH_TL_FEEDBACK: 'view_match_tl_feedback',
  GIVE_SUPERVISOR_FEEDBACK: 'give_supervisor_feedback',
  VIEW_SUPERVISOR_FEEDBACK: 'view_supervisor_feedback',
  SHOW_ALL_MATCHES: 'show_all_matches',

  ADD_PROJECT: 'add_project',
  EDIT_PROJECT: 'edit_project',
  DELETE_PROJECT: 'delete_project',
  VIEW_ALL_PROJECTS: 'view_all_projects',
  ADD_TASK: 'add_task',
  EDIT_TASK: 'edit_task',
  DELETE_TASK: 'delete_task',
  VIEW_ALL_TASKS: 'view_all_tasks',
};

const normalizeRoleKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');

/**
 * Normalized key for the Settings → User Roles matrix (`team-leader` / `teamLeader` → `teamleader`).
 * Permissions are keyed by **auth role** only, not job type.
 */
export const getEffectiveMatrixRoleKey = (user) => {
  if (!user || typeof user !== 'object') return '';
  return normalizeRoleKey(user.role);
};

/** Teams / Matches list and detail routes (legacy API role gate; prefer `hasAnyPermissionForModule`). */
const TEAMS_MATCHES_ROUTE_ROLES = new Set(['admin', 'employee', 'teamleader', 'hod', 'manager']);

export const canAccessTeamsRoutes = (role) => TEAMS_MATCHES_ROUTE_ROLES.has(normalizeRoleKey(role));

export const canAccessMatchesRoutes = (role) => TEAMS_MATCHES_ROUTE_ROLES.has(normalizeRoleKey(role));

/**
 * All feature keys under a Settings → User Roles module. Used to show nav/dashboard tiles when
 * the signed-in role has at least one permission in that module.
 */
export const MODULE_PERMISSION_FEATURES = {
  Assets: [
    'add_assets',
    'edit_assets',
    'checkout',
    'checkin',
    'view_all_assets',
    'delete_assets',
  ],
  'My Assets': ['view_assigned_assets'],
  'Asset Handover': ['view_my_asset_handover'],
  Teams: [
    'add_team',
    'edit_team',
    'delete_team',
    'show_all_teams',
    'view_team_feedback',
    'view_team_event_feedback',
  ],
  Matches: [
    'add_match',
    'edit_match',
    'give_match_tl_feedback',
    'view_match_tl_feedback',
    'give_supervisor_feedback',
    'view_supervisor_feedback',
    'show_all_matches',
  ],
  People: [
    'add_employee',
    'edit_employee',
    'delete_employee',
    'manage_employee_permissions',
  ],
  'Project Management': [
    'add_project',
    'edit_project',
    'delete_project',
    'view_all_projects',
    'add_task',
    'edit_task',
    'delete_task',
    'view_all_tasks',
  ],
  Reports: ['view_asset_reports', 'view_performance_reports'],
};

/** Project Management feature keys, split by page (Projects list vs Tasks list). */
export const PROJECT_MANAGEMENT_PROJECT_FEATURES = [
  PERMISSIONS.ADD_PROJECT,
  PERMISSIONS.EDIT_PROJECT,
  PERMISSIONS.DELETE_PROJECT,
  PERMISSIONS.VIEW_ALL_PROJECTS,
];

export const PROJECT_MANAGEMENT_TASK_FEATURES = [
  PERMISSIONS.ADD_TASK,
  PERMISSIONS.EDIT_TASK,
  PERMISSIONS.DELETE_TASK,
  PERMISSIONS.VIEW_ALL_TASKS,
];

/**
 * True if the role has at least one allowed feature in the module per Settings → User Roles matrix,
 * plus the signed-in user's individual overrides (same keys as User Roles).
 * Does not fall back to JWT / static ROLE_PERMISSIONS — otherwise Teams/Matches could stay visible
 * when every matrix checkbox is off for that role.
 */
export const hasAnyPermissionForModule = (role, matrix, moduleName) => {
  const keys = MODULE_PERMISSION_FEATURES[moduleName];
  if (!Array.isArray(keys) || keys.length === 0) return false;
  const normalizedRole = normalizeRoleKey(role);

  const sessionUser = getCurrentUser();
  const sessionRole = sessionUser ? normalizeRoleKey(sessionUser.role) : '';
  const authMatchesRoleParam = sessionRole && sessionRole === normalizedRole;
  const bucketKey =
    authMatchesRoleParam && sessionUser ? getEffectiveMatrixRoleKey(sessionUser) : normalizedRole;
  const bucket = matrix?.[bucketKey] || {};

  const overridesRaw = sessionUser?.permissionOverrides;
  const overrides =
    overridesRaw instanceof Map
      ? Object.fromEntries(overridesRaw.entries())
      : overridesRaw && typeof overridesRaw === 'object' && !Array.isArray(overridesRaw)
        ? overridesRaw
        : {};

  return keys.some((rawKey) => {
    const feature = String(rawKey || '').trim().toLowerCase();
    if (!feature) return false;

    const directOverride = String(overrides[feature] || '').trim().toLowerCase();
    if (directOverride === 'deny') return false;
    if (directOverride === 'allow') return true;

    if (Object.prototype.hasOwnProperty.call(bucket, feature)) {
      return toAllowedBoolean(bucket[feature]);
    }

    return false;
  });
};

// Role-based permissions mapping
const ROLE_PERMISSIONS = {
  admin: [
    PERMISSIONS.ADD_ASSETS,
    PERMISSIONS.EDIT_ASSETS,
    PERMISSIONS.CHECKOUT,
    PERMISSIONS.CHECKIN,
    PERMISSIONS.ADD_EMPLOYEE,
    PERMISSIONS.EDIT_EMPLOYEE,
    PERMISSIONS.DELETE_EMPLOYEE,
    PERMISSIONS.MANAGE_EMPLOYEE_PERMISSIONS,
    PERMISSIONS.VIEW_ALL_ASSETS,
    PERMISSIONS.VIEW_ASSIGNED_ASSETS,
    PERMISSIONS.VIEW_MY_ASSET_HANDOVER,
    PERMISSIONS.DELETE_ASSETS,
    PERMISSIONS.VIEW_ASSET_REPORTS,
    PERMISSIONS.VIEW_PERFORMANCE_REPORTS,
    PERMISSIONS.ADD_TEAM,
    PERMISSIONS.EDIT_TEAM,
    PERMISSIONS.DELETE_TEAM,
    PERMISSIONS.VIEW_TEAM_EVENT_FEEDBACK,
    PERMISSIONS.SHOW_ALL_TEAMS,
    PERMISSIONS.ADD_MATCH,
    PERMISSIONS.EDIT_MATCH,
    PERMISSIONS.GIVE_MATCH_TL_FEEDBACK,
    PERMISSIONS.VIEW_MATCH_TL_FEEDBACK,
    PERMISSIONS.GIVE_SUPERVISOR_FEEDBACK,
    PERMISSIONS.VIEW_SUPERVISOR_FEEDBACK,
    PERMISSIONS.SHOW_ALL_MATCHES,
    PERMISSIONS.ADD_PROJECT,
    PERMISSIONS.EDIT_PROJECT,
    PERMISSIONS.DELETE_PROJECT,
    PERMISSIONS.VIEW_ALL_PROJECTS,
    PERMISSIONS.ADD_TASK,
    PERMISSIONS.EDIT_TASK,
    PERMISSIONS.DELETE_TASK,
    PERMISSIONS.VIEW_ALL_TASKS,
  ],
  employee: [
    PERMISSIONS.VIEW_ASSIGNED_ASSETS,
    PERMISSIONS.VIEW_MY_ASSET_HANDOVER,
    PERMISSIONS.GIVE_MATCH_TL_FEEDBACK,
    PERMISSIONS.VIEW_MATCH_TL_FEEDBACK,
    PERMISSIONS.VIEW_SUPERVISOR_FEEDBACK,
  ],
  teamleader: [
    PERMISSIONS.VIEW_ASSIGNED_ASSETS,
    PERMISSIONS.VIEW_MY_ASSET_HANDOVER,
    PERMISSIONS.GIVE_MATCH_TL_FEEDBACK,
    PERMISSIONS.VIEW_MATCH_TL_FEEDBACK,
    PERMISSIONS.GIVE_SUPERVISOR_FEEDBACK,
    PERMISSIONS.VIEW_SUPERVISOR_FEEDBACK,
  ],
  hod: [
    PERMISSIONS.VIEW_ASSIGNED_ASSETS,
    PERMISSIONS.VIEW_MY_ASSET_HANDOVER,
    PERMISSIONS.VIEW_MATCH_TL_FEEDBACK,
    PERMISSIONS.VIEW_SUPERVISOR_FEEDBACK,
    PERMISSIONS.SHOW_ALL_MATCHES,
  ],
  // New mid-level role (Admin > HOD > Manager > Team Leader > Employee).
  // Baseline matches other asset-holders; everything else is granted via the User Roles matrix.
  manager: [
    PERMISSIONS.VIEW_ASSIGNED_ASSETS,
    PERMISSIONS.VIEW_MY_ASSET_HANDOVER,
  ],
};

/**
 * Check if user has a specific permission
 * @param {string} role - User role (admin/employee)
 * @param {string} permission - Permission to check
 * @returns {boolean}
 */
export const hasPermission = (role, permission) => {
  if (!role || !permission) return false;
  const roleKey = normalizeRoleKey(role);
  const permissions = ROLE_PERMISSIONS[roleKey] || [];
  return permissions.includes(permission);
};

/**
 * Get current user object from localStorage
 * @returns {object|null}
 */
const getCurrentUser = () => {
  try {
    const userStr = sessionStorage.getItem('user');
    if (!userStr) return null;
    return JSON.parse(userStr);
  } catch (error) {
    return null;
  }
};

export const toAllowedBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1' || normalized === 'yes') return true;
    if (normalized === 'false' || normalized === '0' || normalized === 'no') return false;
  }
  return Boolean(value);
};

/**
 * Normalize possible permission payloads into feature keys.
 * Supports string arrays and permission objects from API responses.
 * @param {any} rawPermissions
 * @returns {string[]}
 */
const normalizePermissionFeatures = (rawPermissions) => {
  if (!Array.isArray(rawPermissions)) return [];

  return rawPermissions
    .map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') {
        if (Object.prototype.hasOwnProperty.call(item, 'allowed') && !toAllowedBoolean(item.allowed)) {
          return null;
        }
        return item.feature || item.permission || item.name || item.key || null;
      }
      return null;
    })
    .filter(Boolean);
};

/**
 * Resolve permissions from user payload if available.
 * @param {object|null} user
 * @returns {string[]}
 */
const getUserPermissionFeatures = (user) => {
  if (!user || typeof user !== 'object') return [];

  // Support multiple common payload shapes.
  const candidates = [
    user.permissions,
    user.permissionFeatures,
    user.allowedPermissions,
    user.features,
  ];

  for (const candidate of candidates) {
    const normalized = normalizePermissionFeatures(candidate);
    if (normalized.length > 0) return normalized;
  }

  return [];
};

/**
 * Get current user role from localStorage
 * @returns {string|null}
 */
export const getCurrentUserRole = () => {
  const user = getCurrentUser();
  return user?.role || null;
};

/**
 * Build `{ [roleLower]: { [featureLower]: boolean } }` from User Roles / GET permissions responses.
 * @param {any[]} permissionRows
 * @returns {Record<string, Record<string, boolean>>}
 */
export const buildRoleFeatureMatrix = (permissionRows) => {
  if (!Array.isArray(permissionRows)) return {};
  const next = {};

  permissionRows.forEach((permission) => {
    const feature = String(permission?.feature || '').trim().toLowerCase();
    if (!feature) return;
    const permissionRoles = permission?.roles;
    const roleEntries = Array.isArray(permissionRoles)
      ? permissionRoles.flatMap((entry) => {
          if (typeof entry === 'string') return [[entry, true]];
          if (entry && typeof entry === 'object') {
            return [[entry.role || entry.name, entry.allowed ?? entry.isAllowed ?? entry.value ?? true]];
          }
          return [];
        })
      : permissionRoles && typeof permissionRoles === 'object'
      ? Object.entries(permissionRoles)
      : [];
    if (roleEntries.length === 0) return;

    roleEntries.forEach(([role, isAllowed]) => {
      const normalizedRole = normalizeRoleKey(role);
      if (!normalizedRole) return;
      if (!next[normalizedRole]) next[normalizedRole] = {};
      next[normalizedRole][feature] = toAllowedBoolean(isAllowed);
    });
  });

  return next;
};

/**
 * Resolve feature(s) for a role using the Settings matrix plus the signed-in user's
 * effective permissions and People-page overrides (when `role` matches the session role).
 *
 * Previously the matrix alone won whenever a key existed, so an individual **allow** for
 * e.g. Edit Team did not show in the UI if the role default in Settings was off.
 *
 * @param {string|null|undefined} role
 * @param {Record<string, Record<string, boolean>>|null|undefined} matrix
 * @param {string|string[]} featureKeys
 */
export const canFeatureForRole = (role, matrix, featureKeys) => {
  const keys = Array.isArray(featureKeys) ? featureKeys : [featureKeys];
  const normalizedRole = normalizeRoleKey(role);

  const sessionUser = getCurrentUser();
  const sessionRole = normalizeRoleKey(sessionUser?.role);
  const authMatchesRoleParam = sessionRole && sessionRole === normalizedRole;
  const bucketRoleKey =
    authMatchesRoleParam && sessionUser ? getEffectiveMatrixRoleKey(sessionUser) : normalizedRole;
  const bucket = matrix?.[bucketRoleKey] || {};

  return keys.some((rawKey) => {
    const feature = String(rawKey || '').trim().toLowerCase();
    if (!feature) return false;

    if (!authMatchesRoleParam) {
      if (Object.prototype.hasOwnProperty.call(bucket, feature)) {
        return toAllowedBoolean(bucket[feature]);
      }
      return hasPermission(role, feature);
    }

    const overridesRaw = sessionUser?.permissionOverrides;
    const overrides =
      overridesRaw && typeof overridesRaw === 'object' && !Array.isArray(overridesRaw)
        ? overridesRaw
        : {};
    const directOverride = String(overrides[feature] || '').trim().toLowerCase();
    if (directOverride === 'deny') return false;
    if (directOverride === 'allow') return true;

    // User Roles matrix for the signed-in role must override ROLE_PERMISSIONS defaults (e.g. admin)
    // so toggling features off in Settings actually hides modules for that admin session.
    if (Object.prototype.hasOwnProperty.call(bucket, feature)) {
      return toAllowedBoolean(bucket[feature]);
    }

    // Matrix has at least one feature row for this role (from GET /permissions). If this feature
    // is absent, the role was not granted it (sparse storage / omitted false keys) — do not fall
    // back to JWT or ROLE_PERMISSIONS or users would keep access from stale tokens.
    const matrixDefinesThisRole = Object.keys(bucket).length > 0;
    if (matrixDefinesThisRole) {
      return false;
    }

    if (can(feature)) return true;

    return hasPermission(role, feature);
  });
};

/**
 * Check if current user has permission
 * @param {string} permission - Permission to check
 * @returns {boolean}
 */
export const can = (permission, options = {}) => {
  const user = getCurrentUser();
  const role = user?.role || null;
  if (!permission) return false;

  if (Array.isArray(options.roles) && options.roles.length > 0 && !options.roles.includes(role)) {
    return false;
  }

  const overridesRaw = user?.permissionOverrides;
  const overrides =
    overridesRaw && typeof overridesRaw === 'object' && !Array.isArray(overridesRaw)
      ? overridesRaw
      : {};
  const directOverride = String(overrides[permission] || '').trim().toLowerCase();
  if (directOverride === 'allow') return true;
  if (directOverride === 'deny') return false;

  const userPermissions = getUserPermissionFeatures(user);
  if (userPermissions.length > 0) {
    return userPermissions.includes(permission);
  }

  return hasPermission(role, permission);
};

/**
 * Inventory Assets module (list/checkout/add/…) — Settings matrix and/or session payload from login.
 * Keeps dashboard/sidebar tiles in sync when GET /permissions succeeds or when JWT lists features.
 */
export const hasAssetsInventoryModuleAccess = (role, matrix) => {
  if (hasAnyPermissionForModule(role, matrix, 'Assets')) return true;
  const feats = MODULE_PERMISSION_FEATURES.Assets || [];
  return feats.some((f) => can(f));
};

/**
 * People module — Settings matrix and/or session features (`add_employee`, `edit_employee`, etc.).
 */
export const hasPeopleModuleAccess = (role, matrix) => {
  if (hasAnyPermissionForModule(role, matrix, 'People')) return true;
  const feats = MODULE_PERMISSION_FEATURES.People || [];
  return feats.some((f) => can(f));
};

/**
 * Project Management module — Settings matrix and/or session features
 * (`add_project`, `view_all_projects`, `add_task`, …). Controls the sidebar group.
 */
export const hasProjectManagementModuleAccess = (role, matrix) => {
  if (hasAnyPermissionForModule(role, matrix, 'Project Management')) return true;
  const feats = MODULE_PERMISSION_FEATURES['Project Management'] || [];
  return feats.some((f) => can(f));
};

/**
 * Which blocks to show on the Reports hub (Asset Reports vs Performance Reports toggles).
 *
 * @returns {{ showAssetReports: boolean, showPerformanceReports: boolean }}
 */
export const getReportSectionVisibility = (role, matrix) => {
  return {
    showAssetReports: canFeatureForRole(role, matrix, [PERMISSIONS.VIEW_ASSET_REPORTS]),
    showPerformanceReports: canFeatureForRole(role, matrix, [PERMISSIONS.VIEW_PERFORMANCE_REPORTS]),
  };
};

/** Base path for People routes under admin vs employee layout. */
export const getPeopleBasePath = (pathname) =>
  String(pathname || '').startsWith('/employee') ? '/employee/people' : '/admin/people';

/**
 * Check if current user is admin
 * @returns {boolean}
 */
export const isAdmin = () => {
  return getCurrentUserRole() === 'admin';
};

/**
 * Check if current user is employee
 * @returns {boolean}
 */
export const isEmployee = () => {
  return getCurrentUserRole() === 'employee';
};

